import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ThemeContext } from '../hooks/ThemeContext';
import { CartContext } from '../hooks/CartContext';
import { UserContext } from '../hooks/UserContext';
import SectionTitle from '../components/common/SectionTitle';
import toast from 'react-hot-toast';
import { api } from '../services/baseUrl';
import { formatCurrency } from '../utils/formatters';

const PaymentSuccess = () => {
  const { theme } = useContext(ThemeContext);
  const { clearCart, fetchCart } = useContext(CartContext);
  const { user, refreshUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDark = theme === 'dark';

  const downloadInvoice = async () => {
    if (!orderData?.id_order) return;

    try {
      const response = await api.get(`/invoices/${orderData.id_order}/download`, { responseType: 'blob' });
      const disposition = response.headers?.['content-disposition'];
      let filename = `facture-${orderData.id_order}.pdf`;

      if (disposition) {
        const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
        if (match) {
          filename = decodeURIComponent(match[1] || match[2]);
        }
      }

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Erreur téléchargement facture:', error);
      toast.error('Impossible de télécharger la facture.');
    }
  };
  const [countdown, setCountdown] = useState(5);
  const [orderData, setOrderData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    // Vérifier et traiter le paiement une seule fois
    const processPayment = async () => {
      if (isProcessing || !sessionId) return;
      
      setIsProcessing(true);
      
      try {
        // Vider le panier local et côté serveur
        clearCart();
        await fetchCart();
        
        // Rafraîchir les données utilisateur
        if (refreshUser) {
          await refreshUser({ silent: true });
        }

        // Récupérer les détails de la commande
        const response = await api.get(`/payment/success?session_id=${sessionId}`);
        if (response.data.success) {
          setOrderData(response.data.order);
          toast.success('Paiement réussi ! Redirection vers vos fichiers...', { id: 'payment-success' });
        }
      } catch (error) {
        console.error('Erreur traitement paiement:', error);
        toast.error('Erreur lors du traitement du paiement', { id: 'payment-error' });
      }
    };

    processPayment();
  }, []); // Exécuter UNE SEULE FOIS au montage

  useEffect(() => {
    // Compte à rebours pour redirection automatique
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      // Rediriger vers mes fichiers après le compte à rebours
      navigate('/espace-client?tab=files');
    }
  }, [countdown, navigate]);

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className="container mx-auto px-4 py-12 max-w-5xl">
        
        <SectionTitle 
          title="Paiement Réussi !"
          subtitle="Votre commande a été enregistrée avec succès"
          align="center"
        />

        <div className={`rounded-lg p-12 text-center ${isDark ? 'bg-[#0b0b0c] border border-white/6' : 'bg-white border border-gray-100'}`}>
          
          {/* Icône de succès */}
          <div className="text-6xl mb-6">✅</div>

          <h2 className="text-2xl font-bold mb-4">Merci pour votre achat !</h2>
          
          <p className={`mb-8 text-lg ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
            Votre commande a été traitée avec succès. Vos fichiers sont maintenant disponibles dans votre espace client.
          </p>

          {/* Compte à rebours */}
          <div className={`rounded-lg p-4 mb-6 ${isDark ? 'bg-blue-900/20 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'}`}>
            <p className={`text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
              Redirection automatique vers vos fichiers dans <span className="font-bold text-xl">{countdown}</span> seconde{countdown > 1 ? 's' : ''}...
            </p>
          </div>

          {/* Détails de la commande */}
          {orderData && (
            <div className={`rounded-lg p-6 mb-8 ${isDark ? 'bg-[#151516]' : 'bg-gray-50'}`}>
              <h3 className="font-semibold mb-4">Détails de votre commande</h3>
              <div className={`text-left space-y-2 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                <p><span className="font-medium">N° de commande :</span> {orderData.order_number}</p>
                <p>
                  <span className="font-medium">Montant total :</span>{' '}
                  {formatCurrency(orderData.total_price ?? 0)}
                </p>
                <p><span className="font-medium">Statut :</span> <span className="text-green-600 font-semibold">Payé</span></p>
              </div>
            </div>
          )}

          {/* Détails */}
          <div className={`rounded-lg p-6 mb-8 ${isDark ? 'bg-[#151516]' : 'bg-gray-50'}`}>
            <h3 className="font-semibold mb-4">Vos fichiers sont prêts !</h3>
            <ul className={`text-left space-y-3 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
              <li className="flex items-start">
                <span className="text-rose-600 font-bold mr-3">✓</span>
                <span>Accédez immédiatement à vos fichiers téléchargeables</span>
              </li>
              <li className="flex items-start">
                <span className="text-rose-600 font-bold mr-3">✓</span>
                <span>Consultez votre commande dans l'espace client</span>
              </li>
              <li className="flex items-start">
                <span className="text-rose-600 font-bold mr-3">✓</span>
                <span>Email de confirmation envoyé à {user?.email}</span>
              </li>
            </ul>
          </div>

          {/* Boutons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/espace-client?tab=files')}
              className="px-6 py-3 bg-rose-600 text-white font-medium rounded-lg hover:bg-rose-700 transition-colors"
            >
              Accéder à mes fichiers
            </button>
            {orderData?.id_order && (
              <button
                type="button"
                onClick={downloadInvoice}
                className="px-6 py-3 bg-white text-rose-600 border border-rose-200 font-medium rounded-lg hover:bg-rose-50 transition-colors"
              >
                Télécharger la facture
              </button>
            )}
            <button
              onClick={() => navigate('/shop')}
              className={`px-6 py-3 border font-medium rounded-lg transition-colors ${
                isDark
                  ? 'border-white/20 text-white hover:bg-[#151516]'
                  : 'border-gray-300 text-gray-800 hover:bg-gray-50'
              }`}
            >
              Continuer vos achats
            </button>
          </div>

          {/* Support */}
          <div className={`mt-8 pt-8 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            <p className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
              Des questions ? <a href="/contact" className="text-rose-600 hover:text-rose-700 font-medium">Contactez-nous</a>
            </p>
          </div>

        </div>

      </div>
    </div>
  );
};

export default PaymentSuccess;
